import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BookingSummary = ({ booking }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const calculateNights = () => {
    if (booking?.nights) return booking?.nights;
    if (booking?.checkIn && booking?.checkOut) {
      const checkIn = new Date(booking?.checkIn);
      const checkOut = new Date(booking?.checkOut);
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    return 0;
  };

  const nights = calculateNights();

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Icon name="ClipboardList" size={24} className="text-primary" />
          Booking Summary
        </h2>
      </div>

      {/* Booking Image */}
      <div className="relative h-64 overflow-hidden">
        <Image
          src={booking?.image}
          alt={booking?.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 right-4">
          <span className="px-4 py-2 bg-success text-success-foreground text-sm font-semibold rounded-full shadow-lg">
            {booking?.type === 'accommodation' ? 'Accommodation' : 'Activity'}
          </span>
        </div>
      </div>

      {/* Booking Details */}
      <div className="p-6 space-y-6">
        {/* Property Name & Location */}
        <div>
          <h3 className="text-2xl font-bold text-foreground mb-2">{booking?.name}</h3>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="MapPin" size={16} />
            <span>{booking?.location}</span>
          </div>
          {booking?.description && (
            <p className="text-sm text-muted-foreground mt-3">{booking?.description}</p>
          )}
        </div>

        {/* Date & Guest Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Calendar" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Check-in</p>
                <p className="text-sm font-semibold text-foreground">{formatDate(booking?.checkIn)}</p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Icon name="Calendar" size={20} className="text-secondary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Check-out</p>
                <p className="text-sm font-semibold text-foreground">{formatDate(booking?.checkOut)}</p>
              </div>
            </div>
          </div>

          {nights > 0 && (
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="Moon" size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="text-sm font-semibold text-foreground">{nights} {nights === 1 ? 'Night' : 'Nights'}</p>
                </div>
              </div>
            </div>
          )}

          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Icon name="Users" size={20} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Guests</p>
                <p className="text-sm font-semibold text-foreground">{booking?.guests} {booking?.guests === 1 ? 'Guest' : 'Guests'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Room Type */}
        {booking?.roomType && (
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-3">
              <Icon name="Hotel" size={20} className="text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Room Type</p>
                <p className="text-sm font-semibold text-foreground">{booking?.roomType}</p>
              </div>
            </div>
          </div>
        )}

        {/* Amenities */}
        {booking?.amenities && booking?.amenities?.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Sparkles" size={16} className="text-primary" />
              Included Amenities
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {booking?.amenities?.map((amenity, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={14} className="text-success" />
                  <span>{amenity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Property Contact</h4>
          <div className="space-y-2">
            {booking?.contactEmail && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Mail" size={14} />
                <span>{booking?.contactEmail}</span>
              </div>
            )}
            {booking?.contactPhone && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Phone" size={14} />
                <span>{booking?.contactPhone}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;